import {
  Component,
  Input,
  Renderer2,
  viewChild,
  input,
  output,
  Output,
  EventEmitter,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem, SortEvent } from '@openng/optimus-ui/api';
import {
  Table,
  TableLazyLoadEvent,
  TableRowExpandEvent,
  TableRowSelectEvent,
  TableRowUnSelectEvent,
} from '@openng/optimus-ui/table';
import { forkJoin } from 'rxjs';
import { Column } from '../../model/common/column';
import { DirtyStatus } from '../../model/common/dirty-status';
import { isNullOrNaN } from '../functions/common-functions';
import { BaseComponent } from './base.component';
import { FilterEvent } from './table-filter.component';

export type GridLazyLoadEvent = {
  lazy: TableLazyLoadEvent;
  selectedColumns?: Column[] | null;
};

/**
 * Base Grid component the contains common functionality for all system tables.
 */
@Component({ changeDetection: ChangeDetectionStrategy.Eager, template: '' })
export class BaseGridComponent<T> extends BaseComponent {
  protected _translateService = inject(TranslateService);
  protected _renderer = inject(Renderer2);
  /*
   * Assign enum to member variable, because it can only access member variables within this component.
   * (This allows Access to DirtyStatus in the html file)
   */
  DirtyStatus = DirtyStatus;

  /**
   * Values to display in the table.
   */
  readonly values = input<T[]>([]);
  /**
   * Column configurations for the table.
   */
  @Input() columns: Column[] = [];
  /**
   * Flag to indicate the table is loading.
   */
  readonly loading = input<boolean>(false);
  /**
   * Flag to indicate the table should be responsive.
   */
  @Input() responsive: boolean = false;
  /**
   * Flag to indicate totals component should be displayed.
   */

  readonly frozenColumns = input<Column[]>([]);

  readonly header = input<String>('');

  readonly minPageSize = input<number>(0);

  /**
   * File name is used when exporting data
   */
  readonly exportFilename = input<string>('');

  private localTotalRecords: number = -1;
  @Input() set totalRecords(trs: number) {
    this.localTotalRecords = trs;
    this.totalPages = this.getTotalPages();
  }

  get totalRecords() {
    return this.localTotalRecords;
  }

  private localNumberOfRows: number = -1;
  @Input() set rows(rows: number) {
    this.localNumberOfRows = rows;
    this.totalPages = this.getTotalPages();
  }

  get rows() {
    return this.localNumberOfRows;
  }

  @Input() set contextMenuItems(items: MenuItem[]) {
    const sep = { separator: true };
    this.contextItems = [...this.contextItems, sep, ...items];
    this.contextItems.forEach((mi) => {
      if (!mi.command) {
        mi.command = this.customContextMenu.bind(this);
      }
    });
  }

  get contextMenuItems() {
    return this.contextItems;
  }

  readonly selected = output<any>();

  readonly unselected = output<any>();

  @Output() filter: EventEmitter<any>;

  @Output() contractDetailEvent: EventEmitter<any>;

  @Output() addNewEvent: EventEmitter<any>;

  readonly rowExpandEvent = output<any>();

  readonly rowEditEvent = output<any>();

  readonly contextMenuEvent = output<any>();

  readonly lazyLoad = output<GridLazyLoadEvent>();

  /**
   * Reference to the primeng table.
   */
  readonly table = viewChild<Table>('dt');

  selectedResult: T | null = null;

  selectedItems: T[] = [];

  localResetTable: boolean = false;

  totalPages = 1;

  selectedColumns: any[] = [];

  lastLoadEvent: TableLazyLoadEvent | null = null;

  contextItems: MenuItem[];

  constructor() {
    super();
    this.filter = new EventEmitter<any>();
    this.contractDetailEvent = new EventEmitter<any>();
    this.addNewEvent = new EventEmitter<any>();
    this.contextItems = [];
  }

  /**
   * Event that fires when a custom context menu is added.
   * @param  $event - original event.
   */
  customContextMenu($event: any) {
    $event.selectedItems = this.selectedItems;
    this.contextMenuEvent.emit($event);
  }

  /**
   * When a row is selected need to fire the selected event with an object so the
   * container knows what to do.
   *
   * @param event - event data from the grid.
   * @param dt - reference to the data table.
   */
  onRowSelect(event: TableRowSelectEvent, dt: Table) {
    this.selected.emit({
      event: event,
      selection: this.selectedResult,
      dt: dt,
    });
  }

  /**
   * Need to let the main container know when a row is unselected.
   * @param event - event from the data table.
   * @param dt - reference to data table.
   */
  onRowUnSelect(event: TableRowUnSelectEvent<object>, dt: Table) {
    if (dt.isRowExpanded(event.data)) {
      dt.toggleRow(event.data, event.originalEvent);
    }
    this.unselected.emit({
      event: event,
      dt: dt,
    });
  }

  /**
   * OnRowExpand is called when a row expand icon is used by the user.  This sets the selected
   * result and emits a row expand event to any consumers.
   * @param  $event - object from primeng expand operation.  Contains the row object in a data field.
   */
  onRowExpand($event: TableRowExpandEvent) {
    this.selectedResult = $event.data;
    this.rowExpandEvent.emit(this.selectedResult);
  }

  /**
   * Custom sort function used to handle sorting code values.  If needed this can be overridden by the implementing component.
   * @param event - primeng sort event.
   */
  customSort(event: SortEvent) {
    event?.data?.sort((data1, data2) => {
      const value1 = data1[event.field!];
      const value2 = data2[event.field!];
      let result = null;

      if (value1 == null && value2 != null) {
        result = -1;
      } else if (value1 != null && value2 == null) {
        result = 1;
      } else if (value1 == null && value2 == null) {
        result = 0;
      } else if (typeof value1 === 'string' && typeof value2 === 'string') {
        result = value1.localeCompare(value2);
      } else {
        result = value1 < value2 ? -1 : value1 > value2 ? 1 : 0;
      }

      return event.order! * result;
    });
  }

  /**
   * Clears the table selections.
   */
  clearSelections() {
    this.selectedItems = [];
  }

  /**
   * Method used to collapse any expanded rows.
   */
  collapseRows() {
    const table = this.table();
    if (!table) {
      return;
    }

    table.value.forEach((value) => {
      if (table.isRowExpanded(value)) {
        table.toggleRow(value);
      }
    });
  }

  /**
   * Method used to collapse all expanded rows except the provided index.
   */
  collapseOtherRows(index: number) {
    const table = this.table();
    if (!table) {
      return;
    }

    table.value.forEach((value) => {
      const idx = table.value.indexOf(value);
      if (index !== idx && table.isRowExpanded(value)) {
        table.toggleRow(value);
      }
    });
  }

  /**
   * Method used to export data to CSV
   * @param dt
   */
  exportCSVFunction() {
    const table = this.table();
    if (!table) {
      return;
    }

    const existingCols = table.columns!;
    const frozenColumns = table.frozenColumns;
    const obsArray: any[] = [];
    if (frozenColumns) {
      frozenColumns.forEach((col) => {
        obsArray.push(this._translateService.get(col.header));
      });
    }

    existingCols.forEach((col) => {
      obsArray.push(this._translateService.get(col.header));
    });

    forkJoin([...obsArray]).subscribe((messages: any[]) => {
      // combine frozen columns and scrollable columns
      if (table.frozenColumns && table.frozenColumns.length > 1) {
        const colArray: Column[] = [];
        table.frozenColumns.forEach((col) => colArray.push(col));
        table.columns?.forEach((col) => colArray.push(col));
        colArray.forEach((col: any, idx: any, arr: any) => {
          col.header = messages[idx];
        });

        table.columns = colArray;
      } else {
        existingCols.forEach((col, idx, arr) => {
          col.header = messages[idx];
        });
      }

      // call export CSV function
      if (this.selectedItems && this.selectedItems.length > 0) {
        table.exportCSV({ selectionOnly: true });
      } else {
        table.exportCSV();
      }

      // reset to the original column sets
      if (table.frozenColumns && table.frozenColumns.length > 1) {
        table.columns = existingCols;
      }
    });
  }

  /**
   * Method used to override the angular table's exportFunction which Table.exportCSV is used
   * @param cell
   */
  exportFunction(cell: any): string {
    if (cell.data instanceof Date) {
      const data: Date = cell.data;
      return data.toLocaleDateString();
    }
    const column = this.columns.find((col) => col.field === cell.field);
    return column?.subField != null ? cell.data[column.subField] : cell.data;
  }

  getErrorMessage(): string[] {
    let messages: string[] = [];
    this.columns.forEach((col) => {
      if (col.formControl && col.formControl.invalid) {
        messages = [...messages, col.validationMessage!];
      }
    });

    return messages;
  }

  lazyLoadEvent(event: TableLazyLoadEvent) {
    // first time searching, find default rows
    if (isNullOrNaN(this.rows) || this.rows === 0) {
      event.rows = this.rows;
    }

    if (this.rows !== event.rows) {
      this.rows = event.rows ?? 0;
    }

    this.lastLoadEvent = event;
    let allowEvent = true;

    if (this.columns) {
      this.columns.forEach((col) => {
        if (col.formControl && col.formControl.invalid) {
          allowEvent = false;
        }
      });
    }

    if (allowEvent) {
      // event.first = First row offset
      // event.rows = Number of rows per page
      // event.sortField = Field name to sort with
      // event.sortOrder = Sort order as number, 1 for asc and -1 for dec
      // filters: FilterMetadata object having field as key and filter value, filter matchMode as value

      // passing selected columns if they are different then the main column list.
      // this allows the service to shape the results coming back to only include the columns
      // we need.
      if (this.selectedColumns === this.columns) {
        this.lazyLoad.emit({ lazy: event, selectedColumns: null });
      } else {
        this.lazyLoad.emit({
          lazy: event,
          selectedColumns: this.selectedColumns,
        });
      }
    }
  }

  /**
   * Method that can be used to reset filters on a grid.
   */
  resetFilters() {
    if (this.columns) {
      this.columns.forEach((c) => {
        c.value = null;
        if (c.formControl) {
          c.formControl.reset();
        }
        this.handleFilter(c, {
          value: null,
          matchMode: c.matchMode,
          field: c.field,
          operator: 'and',
        });
      });

      this.columns = [...this.columns.slice()];
    }
  }

  handleFilter(col: Column, $event: FilterEvent) {
    if (col.formControl) {
      col.formControl.setValue($event.value);
    } else {
      col.value = $event.value;
    }
    this.table()?.filter($event.value, $event.field, $event.matchMode!);
  }

  resetTable() {
    const table = this.table();
    if (!table) {
      return;
    }

    table.lazy = false;
    this.resetFilters();
    table.lazy = true;

    // Reset paginator
    table.first = 0;

    // Reset Sort
    table.sortField = '';
    table.sortOrder = 0;
    table.filters = {};
    table.reset();
  }

  getTotalPages() {
    if (!this.rows || !this.totalRecords || this.rows <= 0) {
      return 1;
    } else {
      return Math.ceil(this.totalRecords / this.rows);
    }
  }

  trackByColumn(index: number, column: Column) {
    return index;
  }

  filterGrid(dt: any, value: any, field: any, matchMode: any) {
    this.selectedItems = [];
    dt.filter(value, field, matchMode);
  }

  getClass(column: Column, ignoreNumber: boolean): string {
    let classList = 'ui-resizable-column ';
    if (ignoreNumber === false && column.formatPattern === 'number') {
      classList += 'number-column ';
    }
    return classList;
  }
}
